import React, { useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment, Center, ContactShadows } from '@react-three/drei';
import { useProgress } from '@react-three/drei';
import * as THREE from 'three';

const MODEL_URL = '/models/nissan_skyline_gtr_r33.glb';

const REMOVE_NAMES = /plane|floor|ground|circle|ring|curve|disc|platform|backdrop|shadow_mesh/i;

function isLikelyFloor(mesh: THREE.Mesh): boolean {
  if (!mesh.geometry) return false;
  mesh.geometry.computeBoundingBox();
  const box = mesh.geometry.boundingBox!;
  const size = new THREE.Vector3();
  box.getSize(size);
  return size.y < 0.04 * Math.max(size.x, size.z) && Math.max(size.x, size.z) > 1.2;
}

function upgradeToPBR(source: THREE.Material): THREE.MeshPhysicalMaterial {
  const src = source as THREE.MeshStandardMaterial;
  return new THREE.MeshPhysicalMaterial({
    color: src.color ?? new THREE.Color(1, 1, 1),
    map: src.map ?? null,
    normalMap: src.normalMap ?? null,
    roughnessMap: src.roughnessMap ?? null,
    metalnessMap: src.metalnessMap ?? null,
    aoMap: src.aoMap ?? null,
    emissiveMap: src.emissiveMap ?? null,
    emissive: src.emissive ?? new THREE.Color(0, 0, 0),
    emissiveIntensity: src.emissiveIntensity ?? 0,
    metalness: src.metalness ?? 0.5,
    roughness: src.roughness ?? 0.4,
    clearcoat: 0.9,
    clearcoatRoughness: 0.08,
    envMapIntensity: 1.8,
    transparent: src.transparent ?? false,
    opacity: src.opacity ?? 1,
    side: src.side ?? THREE.FrontSide,
  });
}

function CarModel() {
  const { scene } = useGLTF(MODEL_URL);
  const disposed = useRef<THREE.Material[]>([]);

  useEffect(() => {
    const toRemove: THREE.Object3D[] = [];

    scene.traverse((child) => {
      if (REMOVE_NAMES.test(child.name)) {
        toRemove.push(child);
        return;
      }
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (isLikelyFloor(mesh)) {
        toRemove.push(mesh);
        return;
      }
      mesh.castShadow = true;
      mesh.receiveShadow = false;
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map((m) => {
          const u = upgradeToPBR(m);
          disposed.current.push(u);
          return u;
        });
      } else if (mesh.material) {
        const u = upgradeToPBR(mesh.material);
        disposed.current.push(u);
        mesh.material = u;
      }
    });

    toRemove.forEach((obj) => obj.parent?.remove(obj));

    return () => {
      disposed.current.forEach((m) => m.dispose());
      disposed.current = [];
    };
  }, [scene]);

  return (
    <Center>
      <primitive object={scene} />
    </Center>
  );
}

interface CarModelViewerProps {
  onProgress?: (p: number) => void;
  onReady?: () => void;
}

function ProgressReporter({ onProgress, onReady }: CarModelViewerProps) {
  const { progress, active } = useProgress();

  useEffect(() => {
    onProgress?.(progress);
    if (!active && progress === 100) onReady?.();
  }, [progress, active, onProgress, onReady]);

  return null;
}

const CarModelViewer: React.FC<CarModelViewerProps> = ({ onProgress, onReady }) => {
  return (
    <div className="relative w-full h-full overflow-hidden bg-[#0a0a0a]">
      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [-4.5, 1.6, 5.5], fov: 36 }}
        gl={{
          antialias: true,
          alpha: false,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.15,
        }}
        style={{ width: '100%', height: '100%' }}
      >
        <color attach="background" args={['#0a0a0a']} />
        <fog attach="fog" args={['#0a0a0a', 22, 50]} />

        <ambientLight intensity={0.18} />
        <directionalLight
          position={[8, 6, 4]}
          intensity={2.4}
          color="#ffeedd"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-bias={-0.0002}
          shadow-camera-near={0.5}
          shadow-camera-far={30}
          shadow-camera-left={-8}
          shadow-camera-right={8}
          shadow-camera-top={6}
          shadow-camera-bottom={-4}
        />
        <directionalLight position={[-7, 3, 2]} intensity={0.55} color="#99b8e8" />
        <directionalLight position={[0.5, 2.5, -10]} intensity={1.8} color="#ddeeff" />
        <pointLight position={[0, -0.4, 1.5]} intensity={0.3} color="#ffffff" distance={7} decay={2} />

        <CarModel />

        <ContactShadows position={[0, -1.15, 0]} opacity={0.6} scale={14} blur={3} far={3.5} />
        <Environment preset="warehouse" background={false} environmentIntensity={0.55} />

        <OrbitControls
          autoRotate
          autoRotateSpeed={0.45}
          enableZoom={false}
          enablePan={false}
          minPolarAngle={Math.PI / 5}
          maxPolarAngle={Math.PI / 2.15}
          dampingFactor={0.06}
          enableDamping
        />
        <ProgressReporter onProgress={onProgress} onReady={onReady} />
      </Canvas>

      {/* Edge gradients — blend into hero background */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-2/5 z-10"
        style={{ background: 'linear-gradient(to right, #0a0a0a 0%, #0a0a0a 15%, rgba(10,10,10,0.6) 50%, transparent 100%)' }} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 z-10"
        style={{ background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)' }} />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 z-10"
        style={{ background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)' }} />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/12 z-10"
        style={{ background: 'linear-gradient(to left, #0a0a0a 0%, transparent 100%)' }} />
    </div>
  );
};

useGLTF.preload(MODEL_URL);

export default CarModelViewer;
